import React, { useState, useEffect } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import './SegmentationEdit.css';
import API_ENDPOINTS from '../../config/apiEndpoints';
import { wcfInstance } from '../../axiosinstance';
import Loader from '../../components/Loader/Loader';
import Swal from 'sweetalert2';

const SegmentationEdit = ({ onClose, initialData, readOnly = false }) => {
    const [loading, setLoading] = useState(false);
    
    // Header properties
    const [criteriaName, setCriteriaName] = useState('');
    const [criteriaType, setCriteriaType] = useState('Lead');
    const [conditionType, setConditionType] = useState('applyall'); // 'applyall' or 'applyany'

    // Condition builder state
    const [fieldName, setFieldName] = useState('');
    const [operator, setOperator] = useState('Contains');
    const [value, setValue] = useState('');

    // Dynamic Options
    const [fieldOptions, setFieldOptions] = useState([]);

    // Standard Operator Options
    const operatorOptions = [
        { label: '---Operator---', value: '' },
        { label: 'Contains', value: '%[A]%' },
        { label: 'Does not contain', value: 'Not Like \'%[A]%\'' },
        { label: 'Starts with', value: '[A]%' },
        { label: 'Does not start with', value: 'Not Like \'[A]%\'' },
        { label: 'Ends with', value: '%[A]' },
        { label: 'Does not end with', value: 'Not Like \'%[A]\'' },
        { label: 'Equal to', value: '=' },
        { label: 'Does not equal', value: '<>' }
    ];

    // Helper to get operator label from value for display
    const getOperatorLabel = (val) => {
        const option = operatorOptions.find(o => String(o.value).toLowerCase() === String(val).toLowerCase());
        return option ? option.label : val;
    };

    // Helper to get operator value from label
    const getOperatorValue = (label) => {
        const option = operatorOptions.find(o => String(o.label).toLowerCase() === String(label).toLowerCase());
        return option ? option.value : label;
    };


    // List of added conditions
    const [conditions, setConditions] = useState([]);

    const API_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');
    const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
    const Custom_Search = `${NEWV3_BASE_URL}/${API_ENDPOINTS.SEGMENTATION.CUSTOME_SEARCH.replace(/^\/+/, '')}`.trim();
    const Get_Field_Name = `${NEWV3_BASE_URL}${API_ENDPOINTS.SEGMENTATION.GET_FIELD_NAME_LIST}`;
    const Save_Update_search_criteria = `${NEWV3_BASE_URL}${API_ENDPOINTS.SEGMENTATION.SAVE_UPDATE_SEARCHCRITERIA}`;

    // Extract Criteria ID
    const criteriaId = initialData?.strCriteriaID || initialData?.ID || initialData?.Id;
    const userId = localStorage.getItem('userId') || '34594';

    // 1. Fetch search criteria details on load
    useEffect(() => {
        if (!criteriaId) return;

        const fetchCriteriaDetails = async () => {
            setLoading(true);
            try {
                const payload = {
                    Token: "-2295521862261168",
                    Details: {
                        userId: parseInt(userId, 10)
                    }
                };
                const response = await wcfInstance.post(Custom_Search, payload);
                
                let criteriaList = [];
                if (response.data && response.data.Details) {
                    criteriaList = Array.isArray(response.data.Details) ? response.data.Details : [];
                } else if (response.data && response.data.d) {
                    let d = response.data.d;
                    if (typeof d === 'string') {
                        try { d = JSON.parse(d); } catch(e) {}
                    }
                    criteriaList = Array.isArray(d) ? d : (d.SearchCriteriaList ? [d.SearchCriteriaList] : []);
                }
                
                if (criteriaList.length > 0) {
                    const listObj = criteriaList.find(c => String(c.ID) === String(criteriaId)) || criteriaList[0];
                    const conditionsList = listObj.SearchCriteriaConditionsList || [];

                    setCriteriaName(listObj.CriteriaName || '');
                    setCriteriaType(listObj.CriteriaType || 'Lead');
                    setConditionType(listObj.ApplyTo || 'applyall');

                    // Map backend conditions into our UI state
                    const mappedConditions = conditionsList.map((cond, idx) => ({
                        id: idx,
                        fieldName: cond.FieldName,
                        fieldType: cond.FieldType || listObj.CriteriaType || 'Lead',
                        operator: cond.Operator, // e.g. '%[A]%'
                        value: cond.Value?.replace(/^'|'$/g, '') || cond.Value // Strip surrounding quotes if present from backend
                    }));

                    setConditions(mappedConditions);
                }
            } catch (error) {
                console.error('Error fetching criteria details:', error);
                Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to fetch criteria details.' });
            } finally {
                setLoading(false);
            }
        };

        fetchCriteriaDetails();
    }, [criteriaId, Custom_Search]);


    // 2. Fetch Field Names whenever criteriaType ("Type") changes
    useEffect(() => {
        const fetchFieldNames = async () => {
            try {
                const payload = {
                    Token: "-2295521862261168",
                    Details: {
                        ParentId: parseInt(userId, 10)
                    }
                };
                const response = await wcfInstance.post(Get_Field_Name, payload);
                
                let fields = [];
                if (response.data && response.data.Details) {
                    fields = Array.isArray(response.data.Details) ? response.data.Details : [];
                } else if (response.data && response.data.d) {
                    let d = response.data.d;
                    if (typeof d === 'string') {
                        try { d = JSON.parse(d); } catch (e) {}
                    }
                    fields = Array.isArray(d) ? d : (d.Table || []);
                }
                
                setFieldOptions(fields);
                
                // Reset selected field if old one is not in new list or if it's currently empty
                if (fields.length > 0) {
                    const firstOpt = fields[0];
                    const firstField = typeof firstOpt === 'string' ? firstOpt : (firstOpt.fieldname || firstOpt.FieldName || firstOpt.Text || firstOpt.Value || firstOpt.ColumnName || firstOpt.Name || '');
                    setFieldName(firstField);
                } else {
                    setFieldName('');
                }
            } catch (error) {
                console.error('Error fetching field names:', error);
            }
        };

        if (criteriaType) {
            fetchFieldNames();
        }
    }, [criteriaType, userId, Get_Field_Name]);


    const handleAddCondition = () => {
        if (!value.trim() || !fieldName || !operator) return;
        
        // Find the matching selected option to extract fieldType
        let fieldType = criteriaType; 
        const selectedOpt = fieldOptions.find(opt => {
            const name = typeof opt === 'string' ? opt : (opt.fieldname || opt.FieldName || opt.ColumnName || opt.Name || opt.Value || '');
            return String(name).toLowerCase() === String(fieldName).toLowerCase();
        });
        
        if (selectedOpt && typeof selectedOpt === 'object') {
            fieldType = selectedOpt.fieldtype || selectedOpt.FieldType || selectedOpt.data_type || criteriaType;
        }

        setConditions([...conditions, { id: Date.now(), fieldName, fieldType, operator, value }]);
        setValue('');
    };

    const handleDeleteCondition = (id) => {
        setConditions(conditions.filter(c => c.id !== id));
    };

    const handleSave = async () => {
        if (!criteriaName.trim()) {
            Swal.fire({ icon: 'warning', title: 'Required', text: 'Please enter a Criteria Name.' });
            return;
        }

        if (criteriaName.length > 40) {
            Swal.fire({ icon: 'warning', title: 'Limit Exceeded', text: 'Criteria Name cannot exceed 40 characters.' });
            return;
        }

        setLoading(true);
        try {
            // Automatically capture any draft condition filled in the input fields but not yet added via the "+" button
            let finalConditions = [...conditions];
            if (value && value.trim() && fieldName && operator) {
                let fieldType = criteriaType; 
                const selectedOpt = fieldOptions.find(opt => {
                    const name = typeof opt === 'string' ? opt : (opt.fieldname || opt.FieldName || opt.ColumnName || opt.Name || opt.Value || '');
                    return String(name).toLowerCase() === String(fieldName).toLowerCase();
                });
                
                if (selectedOpt && typeof selectedOpt === 'object') {
                    fieldType = selectedOpt.fieldtype || selectedOpt.FieldType || selectedOpt.data_type || criteriaType;
                }

                const isDuplicate = conditions.some(c => 
                    String(c.fieldName).toLowerCase() === String(fieldName).toLowerCase() && 
                    String(c.operator).toLowerCase() === String(operator).toLowerCase() && 
                    String(c.value).toLowerCase() === String(value.trim()).toLowerCase()
                );

                if (!isDuplicate) {
                    finalConditions.push({
                        fieldName,
                        fieldType,
                        operator,
                        value: value.trim()
                    });
                }
            }

            const payload = {
                Token: "-2295521862261168",
                Details: {
                    strSearchCriteriaID: criteriaId ? String(criteriaId) : "0",
                    strSearchCriteriaName: criteriaName,
                    strCriteriaType: criteriaType,
                    strApplyTo: conditionType, // 'applyall' or 'applyany'
                    blIsActive: true,
                    strUserID: String(userId),
                    lst_SearchCriteriaConditions: finalConditions.map(c => ({
                        FieldName: c.fieldName,
                        FieldType: c.fieldType || criteriaType,
                        Operator: c.operator,
                        Value: c.value
                    }))
                }
            };

            const response = await wcfInstance.post(Save_Update_search_criteria, payload);
            
            if (response.data && (response.data.Status === 1 || response.data.Status === '1' || response.data.d)) {
                Swal.fire({
                    icon: 'success',
                    title: 'Saved',
                    text: 'Criteria saved successfully',
                    timer: 2000,
                    showConfirmButton: false
                });
                onClose(true);
            } else {
                throw new Error(response.data?.Message || "Failed to save");
            }
        } catch (error) {
            console.error('Error saving criteria:', error);
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save criteria.' });
        } finally {
            setLoading(false);
        }
    };


    return (
        <div className="segmentation-edit-overlay">
            <div className="segmentation-edit-modal">
                {loading && <Loader />}
                <div className="segmentation-edit-header">
                    <h2>Search Criteria</h2>
                    <button className="close-btn" onClick={onClose}><X size={20} /></button>
                </div>

                <div className="segmentation-edit-body">
                    <div className="top-fields-row">
                        <div className="field-group">
                            <label>Type</label>
                            <select value={criteriaType} onChange={(e) => setCriteriaType(e.target.value)} disabled={readOnly}>
                                <option value="All">All</option>
                                <option value="Lead">Lead</option>
                                <option value="Enquiry">Enquiry</option>
                                <option value="Conversion">Conversion</option>
                            </select>
                        </div>
                        <div className="field-group">
                            <label>Criteria Name</label>
                            <div className="input-with-counter">
                                <input
                                    type="text"
                                    value={criteriaName}
                                    onChange={(e) => setCriteriaName(e.target.value)}
                                    placeholder="Criteria Name"
                                    disabled={readOnly}
                                    maxLength={40}
                                />
                                {!readOnly && (
                                    <div className={`char-counter ${criteriaName.length >= 40 ? 'limit-reached' : ''}`}>
                                        {criteriaName.length}/40
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="conditions-radio-group">
                        <label>
                            <input
                                type="radio"
                                name="conditionType"
                                value="applyall"
                                checked={conditionType === 'applyall'}
                                onChange={(e) => setConditionType(e.target.value)}
                                disabled={readOnly}
                            />
                            Apply all of these Conditions
                        </label>
                        <label>
                            <input
                                type="radio"
                                name="conditionType"
                                value="applyany"
                                checked={conditionType === 'applyany'}
                                onChange={(e) => setConditionType(e.target.value)}
                                disabled={readOnly}
                            />
                            Apply any of these Conditions
                        </label>
                    </div>

                    <div className="condition-builder-box">
                        <div className="builder-fields-row">
                            <div className="field-group">
                                <label>Field Name</label>
                                <select value={fieldName} onChange={(e) => setFieldName(e.target.value)} disabled={readOnly}>
                                    {fieldOptions.length === 0 && <option value="">Loading...</option>}
                                    {fieldOptions.map((opt, idx) => {
                                        let label = '';
                                        let val = '';
                                        if (typeof opt === 'string') {
                                            label = opt; val = opt;
                                        } else {
                                            const fName = opt.fieldname || opt.FieldName || opt.Text || opt.ColumnName || opt.Name || opt.Value || `Option ${idx}`;
                                            const fType = opt.fieldtype || opt.FieldType || opt.data_type || '';
                                            label = fType ? `${fName} (${fType})` : fName;
                                            val = opt.fieldname || opt.FieldName || opt.Value || opt.ColumnName || opt.Name || opt.Text || `val_${idx}`;
                                        }
                                        return <option key={idx} value={val}>{label}</option>
                                    })}
                                </select>
                            </div>
                            <div className="field-group">
                                <label>Operator</label>
                                <select value={operator} onChange={(e) => setOperator(e.target.value)} disabled={readOnly}>
                                    {operatorOptions.map((opt, idx) => (
                                        <option key={idx} value={opt.value} disabled={opt.value === ''}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="field-group value-group">
                                <label>Value <span className="helper-text">*Please type {fieldName || 'Value'} String value in text box</span></label>
                                <div className="value-input-row">
                                    <input
                                        type="text"
                                        value={value}
                                        onChange={(e) => setValue(e.target.value)}
                                        placeholder=""
                                        disabled={readOnly}
                                    />
                                    {!readOnly && (
                                        <button className="add-condition-btn" onClick={handleAddCondition}>
                                            <Plus size={16} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="conditions-table-container">
                        <table className="conditions-table">
                            <thead>
                                <tr>
                                    <th>Field Name</th>
                                    <th>Operator</th>
                                    <th>Value</th>
                                    <th>Action</th>
                                    <th>More Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {conditions.map(cond => (
                                    <tr key={cond.id}>
                                        <td>{cond.fieldName}</td>
                                        <td>{getOperatorLabel(cond.operator)}</td>
                                        <td>'{cond.value}'</td>
                                        <td>
                                            {!readOnly && (
                                                <button className="delete-cond-btn" onClick={() => handleDeleteCondition(cond.id)}>
                                                    <Trash2 size={16} />
                                                </button>
                                            )}
                                        </td>
                                        <td></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {!readOnly && (
                    <div className="segmentation-edit-footer">
                        <button className="save-btn" onClick={handleSave}>SAVE</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SegmentationEdit;

